import EsriJSON from 'ol/format/EsriJSON';
import Polygon from 'ol/geom/Polygon';
import { async_forEach, chunk } from '../../../misc/array';
import { Maybe, None, Some } from '../../../misc/option';
import { EPSG } from '../../../projection';
export class FeatureQueryManager {
    constructor(url, identifier) {
        this.url = url;
        this.identifier = identifier;
        this.projection = EPSG(3857);
        this.reader = new EsriJSON();
    }
    _make_post_request(params = {}) {
        const url = this.url;
        let fillUrl = new URL(url.slice(0, 4) === 'http' ? url + '?' : 'https:' + url + '?');
        const serialize = (obj) => {
            let strs = [];
            for (let p in obj) {
                if (obj.hasOwnProperty(p)) {
                    strs.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
                }
            }
            return strs.join('&');
        };
        const response = fetch(fillUrl.toString(), {
            method: "POST",
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json'
            },
            redirect: 'follow',
            body: serialize(params)
        }).catch((reason) => { throw reason; });
        return response;
    }
    _process_response(response) {
        const features = this.reader.readFeatures(response, { featureProjection: this.projection });
        if (features.length > 0)
            return new Some(features);
        return None.instance();
    }
    async _request_features_by_ids(ids) {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        const request = this._make_post_request({
            f: 'pjson',
            inSR: this.projection.split(':')[1],
            outSR: this.projection.split(':')[1],
            returnGeometry: true,
            outfields: this.identifier,
            where: this.identifier + " IN ('" + (ids ? ids.join("','") : "") + "')"
        });
        let r1 = await request.then(r => r.json());
        return this._process_response(r1);
    }
    async _request_feature_at_point(point) {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        const request = this._make_post_request({
            f: 'pjson',
            geometry: point[0] + ',' + point[1],
            geometryType: 'esriGeometryPoint',
            spatialRel: 'esriSpatialRelIntersects',
            returnGeometry: true,
            outfields: this.identifier,
            inSR: this.projection.split(':')[1],
            outSR: this.projection.split(':')[1]
        });
        let r1 = await request.then(r => r.json());
        return this._process_response(r1).map(fs => fs[0]);
    }
    async _request_features_by_geometry(geometry) {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        let coord_string;
        if (geometry instanceof Polygon) {
            let coords = geometry.getCoordinates();
            let coord_strings = coords.map(ring => `[${ring.map(el => `[${el}]`)}]`);
            coord_string = `{"rings": [${coord_strings.join()}]}`;
        }
        else {
            let coords = geometry.getCoordinates();
            coord_string = `{"x": ${coords[0]}, "y": ${coords[1]}}`;
        }
        let params = {
            f: 'pjson',
            geometry: coord_string,
            geometryType: (geometry instanceof Polygon) ? 'esriGeometryPolygon' : 'esriGeometryPoint',
            spatialRel: 'esriSpatialRelIntersects',
            returnGeometry: true,
            outFields: this.identifier,
            inSR: this.projection.split(':')[1],
            outSR: this.projection.split(':')[1],
            where: ""
        };
        const request = this._make_post_request(params);
        const r1 = await request.then(t => t.json());
        let features = this._process_response(r1);
        let addtl = features;
        while (addtl.isSomeAnd(l => l.length > 0)) {
            const current_ids = features.map(flist => flist.map(f => f.get(this.identifier)));
            params.where = `${this.identifier} IS NOT NULL AND ${this.identifier} NOT IN ('${current_ids.unwrapOr([]).join("','")}')`;
            addtl = this._process_response(await this._make_post_request(params).then(r => r.json()));
            features = features.map(l => {
                l.push(...addtl.unwrapOr([]));
                return l;
            });
        }
        return features;
    }
    async get_feature_by_id(id) {
        let result = await this._request_features_by_ids([id]);
        return result.map(a => a[0]);
    }
    async get_features_by_ids(ids) {
        if (ids.length === 0)
            return None.instance();
        if (ids.length <= 1000) {
            let features = await this._request_features_by_ids(ids);
            return features;
        }
        const id_matrix = chunk(ids, 1000);
        let optional_collection = [];
        await async_forEach(id_matrix, async (id_chunk) => {
            const batch = await this._request_features_by_ids(id_chunk);
            optional_collection.push(batch);
        });
        let features = Maybe(optional_collection.flatMap(ofs => ofs.unwrapOr([])));
        return features;
    }
    async get_feature_at_point(point) {
        const feature = await this._request_feature_at_point(point);
        return feature;
    }
    async get_features_by_geometry(geometry) {
        let features = await this._request_features_by_geometry(geometry);
        return features;
    }
}
